import React, { Fragment, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Card,
    CardBody,
    CardFooter,
    Form,
    Button,
    Label,
    Input,
    FormGroup,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import "react-toastify/dist/ReactToastify.css";

const getModuleOperations = (mod) => {
    const str = mod.operation || "access";
    const arr = String(str).split("|").map((s) => s.trim()).filter(Boolean);
    return arr.length ? arr : ["access"];
};

const matrixToPermissionsFromMatrix = (matrix) => {
    const out = [];
    Object.keys(matrix || {}).forEach((moduleId) => {
        const ops = Object.keys(matrix[moduleId] || {}).filter((op) => matrix[moduleId][op]);
        if (ops.length) out.push({ module: moduleId, operations: ops });
    });
    return out;
};

const formatPermissionLabel = (op) =>
    op.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/_/g, " ");

const AddRolePage = () => {
    const navigate = useNavigate();
    const [roleName, setRoleName] = useState("");
    const [modules, setModules] = useState([]);
    const [matrix, setMatrix] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await AxiosInstance().post("/admin/role/get_modules", {});
                if (cancelled) return;
                if (res.data.status && res.data.data) {
                    setModules(res.data.data);
                    const initial = {};
                    res.data.data.forEach((mod) => {
                        const modId = String(mod.module_id);
                        const allowedOps = getModuleOperations(mod);
                        initial[modId] = {};
                        allowedOps.forEach((op) => (initial[modId][op] = false));
                    });
                    setMatrix(initial);
                } else {
                    setError(res.data?.message || "Failed to load modules");
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.response?.data?.message || "Failed to load modules");
                    toast.error(e.response?.data?.message || "Failed to load modules");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const handleChange = (moduleId, operation, checked) => {
        setMatrix((prev) => {
            const next = { ...prev };
            if (!next[moduleId]) next[moduleId] = {};
            next[moduleId] = { ...next[moduleId], [operation]: checked };
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = roleName.trim();
        if (!name) {
            toast.error("Role name is required");
            return;
        }
        setSaving(true);
        try {
            const permissions = matrixToPermissionsFromMatrix(matrix);
            const res = await AxiosInstance().post("/admin/role/create", {
                role_name: name,
                permissions,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Role created");
                navigate("/job/admin/manage-roles");
            } else {
                toast.error(res.data.message || "Create failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Request failed");
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => navigate("/job/admin/manage-roles");

    if (loading) {
        return (
            <Fragment>
                <Breadcrumbs title="Add New Role" mainTitle="Roles & Permissions" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error) {
        return (
            <Fragment>
                <Breadcrumbs title="Add New Role" mainTitle="Roles & Permissions" />
                <Container fluid className="p-4">
                    <Card className="border-0 shadow-sm">
                        <CardBody className="text-center py-5">
                            <p className="text-danger mb-0">{error}</p>
                            <Button color="primary" className="mt-3" onClick={goBack}>
                                Back to Roles
                            </Button>
                        </CardBody>
                    </Card>
                </Container>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Add New Role" mainTitle="Roles & Permissions" />
            <Container fluid={true}>
                <Form onSubmit={handleSubmit} className="theme-form">
                    <Card>
                        <CardBody>
                            <h5 className="mb-4">Role Details</h5>
                            <FormGroup className="mb-4">
                                <Label for="role-name" className="col-form-label m-0 pt-0">
                                    Role Name<span className="text-danger">*</span>
                                </Label>
                                <Input
                                    id="role-name"
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={roleName}
                                    onChange={(e) => setRoleName(e.target.value)}
                                    placeholder="e.g. Recruiter, HR Manager"
                                />
                            </FormGroup>

                            <h5 className="mb-4 mt-4">Permissions</h5>
                            {(!modules || modules.length === 0) ? (
                                <p className="text-muted mb-4">No modules defined. Add modules via Manage Modules.</p>
                            ) : (
                                <div className="d-flex flex-column gap-4 mb-4">
                                    {modules.map((mod) => {
                                        const modId = String(mod.module_id);
                                        const allowedOps = getModuleOperations(mod);
                                        const row = matrix[modId] || {};
                                        return (
                                            <Card key={modId} className="border shadow-none mb-0">
                                                <CardBody className="py-3 px-4">
                                                    <h6 className="mb-3 fw-semibold">{mod.module_name || modId}</h6>
                                                    <div className="row g-3">
                                                        {allowedOps.map((op) => (
                                                            <div key={op} className="col-12 col-sm-6 col-md-4">
                                                                <div className="form-check form-switch">
                                                                    <Input
                                                                        type="switch"
                                                                        className="form-check-input"
                                                                        role="switch"
                                                                        id={`perm-${modId}-${op}`}
                                                                        checked={!!row[op]}
                                                                        onChange={(e) => handleChange(modId, op, e.target.checked)}
                                                                    />
                                                                    <Label className="form-check-label small text-capitalize" htmlFor={`perm-${modId}-${op}`}>
                                                                        {formatPermissionLabel(op)}
                                                                    </Label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        );
                                    })}
                                </div>
                            )}
                        </CardBody>
                        <CardFooter className="text-end">
                            <Button type="button" color="secondary" className="me-2" onClick={goBack}>
                                Cancel
                            </Button>
                            <Button color="primary" type="submit" disabled={saving}>
                                {saving ? "Creating..." : "Create Role"}
                            </Button>
                        </CardFooter>
                    </Card>
                </Form>
            </Container>
            <ToastContainer />
        </Fragment>
    );
};

export default AddRolePage;
